import * as ReactGA from 'react-ga';
import {createBrowserHistory} from 'history';
import Store from '../../redux/Store';
import MyWindow from '../../types/MyWindow';
import Constants from '../../globals/Constants';

declare const window: MyWindow;

interface IGoogleAnalyticsEvent {
   event: string;
   eventCategory: string;
   eventAction: string;
   eventLabel: string;
   eventValue: number;
   tscId: string;
   pageUrl?: string;
}

const history = createBrowserHistory();

const createUserActionEvent = (tscId: string, eventCategory: string, eventAction: string, eventLabel = ''): IGoogleAnalyticsEvent => {
   return {
      event: 'GAEvent',
      eventCategory,
      eventAction,
      eventLabel,
      eventValue: 0,
      tscId
   };
};

const createPageViewEvent = (tscId: string, pageUrl: string): IGoogleAnalyticsEvent => {
   return {
      event: 'virtualPageView',
      eventCategory: 'page',
      eventAction: 'view',
      eventLabel: '',
      eventValue: 0,
      tscId,
      pageUrl
   };
};

const initialize = () => ReactGA.initialize(Constants.googleAnalytics.propertyId);

const trackEvent = (event: IGoogleAnalyticsEvent): void => {
   window.dataLayer = window.dataLayer || [];
   window.dataLayer.push(event);
};

const trackLoadMoreReviewsButtonClicks = (userId: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.loadMoreReviews.category, Constants.googleAnalytics.loadMoreReviews.action));
};

const trackReviewSortingMetricSwitch = (userId: string, action: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.sortReviews.category, action));
};

const trackTrialStarted = (userId: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.trial.category, Constants.googleAnalytics.trial.action));
};

const trackSubscriptionPurchased = (userId: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.subscription.category, Constants.googleAnalytics.subscription.action));
};

const trackPreferenceChanged = (userId: string, action: string, label: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.preferenceChange.category, action, label));
};

const trackReviewByDateChanged = (userId: string, action: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.reviewByDate.category, action));
};

const trackExportComments = (userId: string, action: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.exportComments.category, action));
};

const trackDeleteReview = (userId: string, action: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.deleteReview.category, action));
};

const trackGuestUserLogInWithTechSmithAccount = (userId: string, action: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.guestUserLogIn.category, action));
};

const trackCreateReview = (userId: string, action: string, label: string = ''): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.createReview.category, action, label));
};

const trackCommenting = (userId: string, action: string, label: string = ''): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.commenting.category, action, label));
};

const trackEditReview = (userId: string, label: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.editReview.category, Constants.googleAnalytics.editReview.action, label));
};

const trackVersioning = (userId: string, action: string, label: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.versioning.category, action, label));
};

const trackShareReview = (userId: string, action: string, label: string = ''): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.shareReview.category, action, label));
};

const trackLeaveReview = (userId: string): void => {
   trackEvent(createUserActionEvent(userId, Constants.googleAnalytics.leaveReview.category, Constants.googleAnalytics.leaveReview.action));
};

const trackRumData = (product: string, name: string, time: number): void => {
   ReactGA.timing({
      category: 'SmartPlayerRUM',
      label: product,
      variable: name,
      value: time
   });
};

history.listen(update => {
   const tscId = Store.getState().currentUserId || '';
   trackEvent(createPageViewEvent(tscId, update.pathname));
});

export default {
   initialize,
   trackTrialStarted,
   trackSubscriptionPurchased,
   trackPreferenceChanged,
   trackReviewByDateChanged,
   trackExportComments,
   trackDeleteReview,
   trackGuestUserLogInWithTechSmithAccount,
   trackCreateReview,
   trackCommenting,
   trackEditReview,
   trackVersioning,
   trackShareReview,
   trackLeaveReview,
   trackRumData,
   trackLoadMoreReviewsButtonClicks,
   trackReviewSortingMetricSwitch
};
